import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Minus, ChevronRight, ChevronDown, User, Users, UserCog, Lock } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { Footer } from '../components/Footer';


const FAQS = [
  {
    q: 'Is my luggage insured?',
    a: 'Yes — every booking is automatically covered up to ₹10,000 against loss or damage during transit. You can declare a higher value at booking for additional coverage. Claims are processed within 7 business days.',
  },
  {
    q: 'Where do you currently operate?',
    a: 'We currently serve major Indian metro and tier-1 cities including Bangalore, Mumbai, Delhi NCR, Hyderabad, Chennai, Kolkata, Pune and Ahmedabad. We are expanding to tier-2 cities every quarter.',
  },
  {
    q: 'How fast can my luggage reach the destination?',
    a: 'Standard inter-city delivery takes 24–48 hours from pickup. Intra-city deliveries are typically same-day. You can choose a specific pickup date when booking — we will match the delivery window accordingly.',
  },
  {
    q: 'What happens if pickup or delivery is delayed?',
    a: 'You receive an instant WhatsApp notification when there is any delay, along with a fresh ETA. If the delay is on us beyond 24 hours, you get an automatic partial refund — no need to ask.',
  },
  {
    q: 'How does the OTP delivery work?',
    a: 'When your agent arrives at the drop-off, we send a 6-digit OTP to your registered mobile. The agent enters it on their device to mark the bag as DELIVERED — bags are never handed over without this verification.',
  },
  {
    q: 'Can I book multiple bags in one order?',
    a: 'Absolutely. Add as many bags as you need on the booking page — each bag gets its own QR-coded baggage tag for tracking. Price is calculated on the combined weight × ₹65 plus one flat base fare per booking.',
  },
  {
    q: 'Can I edit my booking after paying?',
    a: 'Yes. Edit pickup time, address, or even add bags until pickup begins. If the new price is higher, we auto-trigger a quick top-up payment. If lower, the difference is queued as a pending refund visible in your dashboard.',
  },
  {
    q: 'How are refunds processed?',
    a: 'Cancellations before pickup are fully refundable. Refunds reflect on the original payment method (Razorpay/UPI/Card) within 5–10 business days. Edit-driven refunds are reviewed and released by our admin team within 24 hours.',
  },
];

const FAQ = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0); // first one open by default
  const [signInOpen, setSignInOpen] = useState(false);

  return (
    <div className="theme-sunset" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top Bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#E8DCC9]/85 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="back-to-home" className="flex items-center">
            <BrandMark size={36} textClass="text-base sm:text-lg text-[#1A1A1A]" />
          </Link>
          <nav className="hidden sm:flex items-center gap-7 text-xs uppercase tracking-[0.2em] text-[#525252]">
            <Link to="/" data-testid="nav-home" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <Link to="/pricing" className="hover:text-[#FF5A1F] transition-colors">Pricing</Link>
            <Link to="/faq" className="text-[#FF5A1F] border-b border-[#FF5A1F] pb-0.5">FAQ</Link>
            <Link to="/track" className="hover:text-[#FF5A1F] transition-colors">Track</Link>
            <Link
              to="/?signin=1"
              data-testid="nav-signin-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white hover:bg-[#FF5A1F] transition-colors text-xs font-bold tracking-wide no-underline"
            >
              <User size={13} /> Sign In
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-5xl mx-auto px-6 sm:px-8 pt-20 pb-12">
        <div className="relative z-10">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#FF5A1F] mb-6 font-semibold">── Frequently Asked</div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] text-[#1A1A1A]">
            Quick<br />
            <span className="text-[#FF5A1F]">answers.</span>
          </h1>
          <p className="mt-6 text-lg max-w-2xl leading-relaxed text-[#525252]">
            Everything you might want to know before your first pickup — coverage, refunds,
            tracking and timing. Can&apos;t find what you need? Drop us a line at{' '}
            <a href="mailto:support@luggagefreejourney.com" className="text-[#FF5A1F] hover:underline">
              support@luggagefreejourney.com
            </a>.
          </p>
        </div>
      </section>

      {/* Accordion */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 pb-20">
        <div className="rounded-2xl border border-[#1A1A1A]/10 bg-white/92 backdrop-blur overflow-hidden shadow-sm">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            const last = i === FAQS.length - 1;
            return (
              <div key={i} className={last ? '' : 'border-b border-[#1A1A1A]/10'}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 px-6 py-6 text-left hover:bg-[#FAF6EE] transition-colors"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-mono font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-lg md:text-xl font-semibold tracking-tight text-[#1A1A1A]">
                      {item.q}
                    </span>
                  </div>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isOpen ? 'bg-[#FF5A1F]' : 'bg-[#FAF6EE] border border-[#1A1A1A]/12'
                  }`}>
                    {isOpen ? <Minus size={16} className="text-white" /> : <Plus size={16} className="text-[#1A1A1A]" />}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-out ${
                    isOpen ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="pl-16 pr-8 text-[#525252] leading-relaxed max-w-3xl">
                    {item.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 text-center">
        <h2 className="relative text-4xl md:text-5xl font-bold tracking-tight text-[#1A1A1A]">Still curious?</h2>
        <p className="relative mt-4 text-base text-[#525252]">Skip the questions — try a booking.</p>
        <button
          data-testid="faq-book-now-btn"
          onClick={() => navigate('/?book=1')}
          className="mt-8 inline-flex items-center gap-2 bg-[#FF5A1F] text-white px-9 py-4 rounded-full text-sm font-bold uppercase tracking-widest hover:bg-[#E64200] hover:scale-[1.02] transition-all shadow-[0_8px_24px_rgba(255,90,31,0.3)]"
        >
          Book a pickup <ChevronRight size={18} />
        </button>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
